import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { routeTree } from './routeTree.gen';
import { ApiProvider, useApiClients } from './app/ApiProvider';
import { CapabilitiesProvider } from './app/CapabilitiesProvider';
import { ThemeProvider } from './app/ThemeProvider';
import { getStoredToken } from './app/auth';
import {
  initTelemetry,
  installGlobalErrorHandlers,
  reportError,
  reportPageView,
  startVitalsCollector,
} from './app/telemetry';
import './styles/global.css';

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// CapabilitiesMount reads ApiClients via the ApiProvider context that
// wraps it, then mounts CapabilitiesProvider for the router subtree.
// Tiny helper so we don't need to add a separate "construct clients
// outside Router" branch. Lives in main.tsx because it's a one-off.
function CapabilitiesMount({ children }: { children: ReactNode }) {
  const clients = useApiClients();
  return (
    <CapabilitiesProvider clients={clients}>{children}</CapabilitiesProvider>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false },
    mutations: { onError: (err) => reportError(err as Error) },
  },
});

// Base URL the typed clients hit. In dev with MSW on (default) this is
// empty so requests resolve against the Vite dev origin and the MSW
// worker intercepts them. When VITE_USE_MSW=false the Vite proxy
// forwards /v1/* to VITE_API_BASE, so the typed clients again use
// relative paths. In production (Cloudflare Pages) VITE_PDM_API_BASE_URL is set
// to the api-gateway origin so the SPA calls the real backend cross-origin
// (the gateway must allow this origin via CORS).
const API_BASE_URL =
  (import.meta.env.VITE_PDM_API_BASE_URL as string | undefined) ?? '';

// Bearer token resolution: prefer the localStorage token written by
// /v1/auth/login on successful sign-in, then fall back to a pasted
// VITE_DEV_TOKEN for the rare case where a developer wants to bypass
// the login flow against a real backend. Returns undefined when
// neither is present — requests go out with no Authorization header
// and the backend's RequireBearer middleware responds with 401.
const DEV_TOKEN = import.meta.env.VITE_DEV_TOKEN as string | undefined;
const getAuthToken = () => getStoredToken() ?? DEV_TOKEN;

async function enableMocksInDev() {
  if (import.meta.env.MODE !== 'development') return;
  // Phase 6 (1/n) — VITE_USE_MSW=false disables MSW so the frontend hits
  // the real backend via the Vite proxy. Default stays "on" so a fresh
  // `npm run dev` still works with no backend.
  if (import.meta.env.VITE_USE_MSW === 'false') return;
  const { worker } = await import('./mocks/browser');
  await worker.start({
    onUnhandledRequest: 'bypass',
    serviceWorker: { url: '/mockServiceWorker.js' },
  });
}

async function registerTokensSwInProd() {
  // Only register the tokens SW in production. In dev, MSW already owns
  // the service-worker slot and registering two workers on different
  // scopes causes ordering surprises. The MSW worker is dev-only;
  // there's no conflict in prod.
  if (import.meta.env.MODE === 'development') return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    await navigator.serviceWorker.register('/tokens-sw.js', { scope: '/' });
  } catch {
    // Registration failure is non-fatal — falls back to network fetches
    // of tokens.css with no caching.
  }
}

// Page-view beacon — router exposes a subscription API that fires every
// time the resolved pathname settles. We subscribe early so the first
// landing also reports.
router.subscribe('onResolved', (e) => {
  reportPageView(e.toLocation.pathname);
});

// Async + event-handler throws don't reach the React ErrorBoundary.
// Install the global listeners early so even pre-mount errors get
// reported through the same telemetry path.
installGlobalErrorHandlers();

Promise.all([
  initTelemetry(),
  startVitalsCollector(),
  enableMocksInDev(),
  registerTokensSwInProd(),
]).then(() => {
  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('#root element missing from index.html');

  createRoot(rootEl).render(
    <StrictMode>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <ApiProvider baseUrl={API_BASE_URL} getAuthToken={getAuthToken}>
            <CapabilitiesMount>
              <RouterProvider router={router} />
            </CapabilitiesMount>
          </ApiProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </StrictMode>,
  );
});
